import { useEffect, useState } from 'react';
import { Icon } from '../../components/Icon.tsx';
import { Button, Dialog, Field } from '../../components/ui.tsx';
import { t } from '../../i18n/index.ts';
import { action } from '../../lib/api.ts';
import { dateTime, day, money, parseMoney } from '../../lib/format.ts';
import { Table } from '../Reports.tsx';
import { useAction, useSave } from './common.tsx';
import { costText, unitCost, withUnit, type Unit } from './Stock.tsx';

type Method = 'value' | 'qty' | 'equal';
const METHODS: { key: Method; icon: 'cash' | 'box' | 'list' }[] = [
  { key: 'value', icon: 'cash' },
  { key: 'qty', icon: 'box' },
  { key: 'equal', icon: 'list' },
];

export interface Landed {
  id: string;
  name: string;
  amount: number;
  method: Method;
  note: string | null;
  to_stock: number;
  to_sold: number;
  at: string;
  bill: string;
  bill_number: number;
  supplier_ref: string | null;
  supplier_name: string;
  by_name: string | null;
  orders: string[];
  lines: { amount: number; to_stock: number; to_sold: number; qty: number; name: string; unit: Unit; order_name: string }[];
}
interface LandedBill {
  id: string;
  name: string;
  supplier_ref: string | null;
  supplier_name: string;
  date: string;
  left: number;
}
interface OrderRow {
  id: string;
  number: number;
  supplier_name: string;
  state: string;
  received_value: number;
  received_at: string | null;
}
interface Preview {
  lines: { order: string; order_name: string; item: string; name: string; unit: Unit; qty: number; value: number; amount: number; to_stock: number; to_sold: number; unit_cost: number; unit_cost_after: number }[];
  to_stock: number;
  to_sold: number;
}

/**
 * The landed costs of an order or of a bill: freight, customs and the like spread over the goods.
 * `onAdd` shows the button to add one.
 */
export function LandedList({ order, bill, billNet, version, onAdd: add }: { order?: string; bill?: string; billNet?: number; version?: number; onAdd?: () => void }) {
  const { data } = useAction<Landed[]>('purchase.landed_costs', { order, bill, v: version ?? 0 });
  // A bill already spread in full has nothing more to give.
  const onAdd = billNet !== undefined && data && data.reduce((s, c) => s + c.amount, 0) >= billNet ? undefined : add;
  if (!data || (!data.length && !onAdd)) return null;
  return (
    <section className="item-moves" aria-labelledby="landed-title">
      <div className="landed-head">
        <h3 id="landed-title">{t('lc.title')}</h3>
        {onAdd ? (
          <Button size="sm" icon="plus" onClick={onAdd}>
            {bill ? t('lc.spread_bill') : t('lc.add')}
          </Button>
        ) : null}
      </div>
      <div className="list-card">
        {data.length ? (
          data.map((c) => (
            <div key={c.id} className="list-row compact-move landed-row">
              <span className="list-icon move-landed" aria-hidden="true">
                <Icon name="truck" size={18} />
              </span>
              <span className="list-main">
                <span className="list-title">
                  {c.name} · {bill ? c.orders.join(', ') : [`BILL-${c.bill_number}`, c.supplier_ref, c.supplier_name].filter(Boolean).join(' · ')}
                </span>
                <span className="list-sub">
                  {[dateTime(c.at), t(`lc.m_${c.method}`), c.by_name, c.note].filter(Boolean).join(' · ')}
                </span>
                <span className="list-sub">
                  {c.lines.map((l) => `${l.name} +${money(l.amount)}`).join(' · ')}
                </span>
              </span>
              <span className="stock-qty">
                <strong>{money(c.amount)}</strong>
                {c.to_sold ? <span className="muted">{t('lc.sold_part', { amount: money(c.to_sold) })}</span> : null}
              </span>
            </div>
          ))
        ) : (
          <p className="muted pad">{t(bill ? 'lc.none_bill' : 'lc.none')}</p>
        )}
      </div>
    </section>
  );
}

/**
 * Spreads a bill for freight, customs or the like over the goods received on one or more
 * purchase orders. Opened from an order (the order chosen) or from a bill (the bill chosen).
 */
export function LandedDialog({ order, bill, onClose, onDone }: { order?: string; bill?: string; onClose: () => void; onDone: () => Promise<void> | void }) {
  const { busy, run } = useSave();
  const { data: bills } = useAction<LandedBill[]>('purchase.landed_bills');
  const { data: orders } = useAction<OrderRow[]>('purchase.orders', { filter: 'all' });
  const received = (orders ?? []).filter((o) => o.state === 'partial' || o.state === 'received');
  const [billId, setBillId] = useState(bill ?? '');
  const [picked, setPicked] = useState<string[]>(order ? [order] : []);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<Method>('value');
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const chosen = bills?.find((b) => b.id === billId) ?? null;
  const left = chosen?.left ?? 0;
  const amountN = amount.trim() === '' ? left : parseMoney(amount);
  const amountError = amountN === null ? t('buy.money_rule') : chosen && amountN > left ? t('lc.too_much', { left: money(left) }) : null;

  useEffect(() => {
    if (!bills || billId || bill) return;
    if (bills.length === 1) setBillId(bills[0]!.id);
  }, [bills, billId, bill]);

  useEffect(() => {
    setPreview(null);
    setProblem(null);
    if (!picked.length || !amountN || amountN <= 0 || amountError) return;
    let live = true;
    const timer = setTimeout(() => {
      action<Preview>('purchase.landed_preview', { orders: picked, amount: amountN, method }).then(
        (p) => live && setPreview(p),
        (e: Error) => live && setProblem(e.message),
      );
    }, 250);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [picked.join(','), amountN, method, amountError]);

  const save = () =>
    void run(async () => {
      const lc = await action<{ name: string }>('purchase.add_landed_cost', { bill: billId, orders: picked, amount: amountN, method, note: note.trim() || null });
      await onDone();
      onClose();
      return lc;
    }, t('lc.added'));

  const q = search.trim().toLowerCase();
  const shown = received.filter((o) => picked.includes(o.id) || !q || `po-${o.number} ${o.supplier_name}`.toLowerCase().includes(q)).slice(0, 40);
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const noBills = bills !== null && !bills.length && !bill;

  return (
    <Dialog
      title={t('lc.add_title')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <span className="muted small">{preview ? t('lc.foot', { stock: money(preview.to_stock), sold: money(preview.to_sold) }) : null}</span>
          <Button variant="primary" icon="check" onClick={save} busy={busy} disabled={!billId || !picked.length || !amountN || !!amountError || !preview}>
            {t('lc.save', { amount: money(amountN ?? 0) })}
          </Button>
        </>
      }
    >
      <div className="form-stack landed-form">
        <p className="muted">{t('lc.intro')}</p>
        {noBills ? (
          <p className="callout callout-warn" role="alert">
            <Icon name="alert" size={16} /> {t('lc.no_bills')}
          </p>
        ) : null}
        <div className="form-grid">
          <Field label={t('lc.bill')} hint={chosen ? t('lc.bill_left', { left: money(left) }) : t('lc.bill_hint')}>
            <select className="input" value={billId} onChange={(e) => setBillId(e.target.value)} disabled={!!bill}>
              {!billId ? <option value="">{t('lc.choose_bill')}</option> : null}
              {(bills ?? []).map((b) => (
                <option key={b.id} value={b.id}>
                  {[b.name, b.supplier_ref, b.supplier_name, money(b.left)].filter(Boolean).join(' · ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('lc.amount')} hint={t('lc.amount_hint')} error={amountError}>
            <input className="input" inputMode="decimal" value={amount} placeholder={left ? String(left / 100) : ''} onChange={(e) => setAmount(e.target.value)} />
          </Field>
        </div>
        <div>
          <span className="field-label landed-label" id="lc-method">
            {t('lc.method')}
          </span>
          <div className="segmented" role="radiogroup" aria-labelledby="lc-method">
            {METHODS.map((m) => (
              <button key={m.key} role="radio" aria-checked={method === m.key} className={method === m.key ? 'active' : ''} onClick={() => setMethod(m.key)}>
                <Icon name={m.icon} size={16} /> {t(`lc.m_${m.key}`)}
              </button>
            ))}
          </div>
          <p className="muted small">{t(`lc.m_${method}_hint`)}</p>
        </div>
        <fieldset className="landed-orders">
          <legend>{t('lc.orders')}</legend>
          {received.length > 6 ? (
            <input className="input" type="search" value={search} placeholder={t('lc.find_order')} onChange={(e) => setSearch(e.target.value)} aria-label={t('lc.find_order')} />
          ) : null}
          <div className="list-card">
            {orders === null ? null : shown.length ? (
              shown.map((o) => (
                <label key={o.id} className="list-row landed-order">
                  <input type="checkbox" checked={picked.includes(o.id)} onChange={() => toggle(o.id)} />
                  <span className="list-main">
                    <span className="list-title">
                      PO-{o.number} · {o.supplier_name}
                    </span>
                    <span className="list-sub">{o.received_at ? t('lc.received_on', { day: day(o.received_at.slice(0, 10)) }) : null}</span>
                  </span>
                  <strong className="tabular">{money(o.received_value)}</strong>
                </label>
              ))
            ) : (
              <p className="muted pad">{t('lc.no_orders')}</p>
            )}
          </div>
        </fieldset>
        <Field label={t('lc.note')} hint={t('lc.note_hint')}>
          <input className="input" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
        </Field>
        {problem ? (
          <p className="callout callout-warn" role="alert">
            <Icon name="alert" size={16} /> {problem}
          </p>
        ) : null}
        {preview ? (
          <section aria-labelledby="lc-preview">
            <h3 id="lc-preview">{t('lc.preview')}</h3>
            <Table
              head={[t('lc.c_item'), t('lc.c_received'), t('lc.c_share'), t('lc.c_stock'), t('lc.c_sold'), t('lc.c_unit')]}
              phoneHide={[1, 3, 4]}
              rows={preview.lines.map((l) => ({
                key: `${l.order}:${l.item}`,
                cells: [
                  <span className="landed-item">
                    <strong>{l.name}</strong>
                    {picked.length > 1 ? <span className="muted small">{l.order_name}</span> : null}
                  </span>,
                  <span className="tabular">{withUnit(l.qty, l.unit)}</span>,
                  <span className="tabular strong">{money(l.amount)}</span>,
                  <span className="tabular">{money(l.to_stock)}</span>,
                  <span className={`tabular ${l.to_sold ? 'text-warn' : 'muted'}`}>{l.to_sold ? money(l.to_sold) : '—'}</span>,
                  <span className="tabular landed-cost">
                    <span className="muted">{costText(l.unit_cost)}</span> → <strong>{unitCost(l.unit_cost_after, l.unit)}</strong>
                  </span>,
                ],
              }))}
            />
            {preview.to_sold ? <p className="muted small">{t('lc.sold_hint')}</p> : null}
          </section>
        ) : null}
      </div>
    </Dialog>
  );
}
